'use client'

import { Children, cloneElement, isValidElement } from 'react'

import { Select, SelectPopover, SelectTrigger, SelectValue, Label, ListBox, ListBoxItem } from '@heroui/react'
import type { ReactNode, ReactElement, Key } from 'react'

/**
 * Backward-compatible Select wrapper for HeroUI v3.
 *
 * v2 usage: <Select><SelectItem key="a">A</SelectItem></Select>
 * v3 requires: Select.Root > Select.Trigger + Select.Popover > ListBox > ListBoxItem
 *
 * This wrapper accepts v2-style children (ListBoxItem) and wraps them in v3 compound structure.
 */
interface CompatSelectProps {
  children: ReactNode | ((item: any) => ReactNode)
  label?: ReactNode
  placeholder?: string
  variant?: string
  size?: string
  color?: string
  description?: ReactNode
  selectedKeys?: Iterable<Key>
  onSelectionChange?: (keys: any) => void
  /** @deprecated Use onSelectionChange instead (v2 compat) */
  onChange?: (e: any) => void
  isDisabled?: boolean
  isRequired?: boolean
  /** @deprecated Use isRequired instead (v2 compat) */
  required?: boolean
  isInvalid?: boolean
  errorMessage?: ReactNode
  className?: string
  'aria-label'?: string
  'data-testid'?: string
  selectionMode?: string
  fullWidth?: boolean
  /** Additional HTML/ARIA props */
  id?: string
  name?: string
  onBlur?: (...args: any[]) => void
  onClose?: () => void
  isLoading?: boolean
  value?: string
  items?: any
  [key: string]: any
}

export function CompatSelect({
  children,
  label,
  placeholder,
  variant,
  size,
  color,
  description,
  selectedKeys,
  onSelectionChange,
  onChange,
  isDisabled,
  isRequired,
  required,
  isInvalid,
  errorMessage,
  className,
  'aria-label': ariaLabel,
  'data-testid': testId,
  fullWidth,
  id,
  name,
  onBlur,
  onClose,
  isLoading,
  value,
  items,
  ...rest
}: CompatSelectProps) {
  // v2-style onChange expects an event with `target.value`; bridge v3's
  // onSelectionChange (a Set<Key> or single Key) so existing consumers that
  // only pass `onChange` actually update their state. Without this bridge the
  // Select silently drops selections (e.g. the country picker on /pay).
  // HeroUI v3 single-select passes onSelectionChange a single Key (or null),
  // NOT a Set. Normalize to a Set so caller handlers that do Array.from(keys)[0]
  // work; a bare string would otherwise iterate char-by-char and commit only
  // the first character of the id (e.g. the shop-location cuid -> "c").
  const normalizeKeys = (keys: unknown): Set<Key> => {
    if (keys && typeof keys === 'object' && 'size' in (keys as Set<Key>)) {
      return keys as Set<Key>
    }
    return keys === null || keys === undefined ? new Set<Key>() : new Set<Key>([keys as Key])
  }
  const resolvedSelectionChange = onSelectionChange
    ? (keys: unknown) => onSelectionChange(normalizeKeys(keys))
    : onChange
      ? (keys: unknown) => {
          const key = Array.from(normalizeKeys(keys))[0]
          if (key !== undefined) {
            onChange({ target: { value: String(key) } })
          }
        }
      : undefined
  // HeroUI v3 Select is single-select: it expects `selectedKey` (singular), not
  // `selectedKeys`. Callers passing v2-style `selectedKeys={['CA']}` would see
  // the default never render and selections silently drop. Translate here.
  const firstKeyRaw = selectedKeys ? Array.from(selectedKeys as Iterable<Key>)[0] : null
  const firstKey: string | number | null =
    firstKeyRaw === null || firstKeyRaw === undefined
      ? null
      : typeof firstKeyRaw === 'bigint'
        ? String(firstKeyRaw)
        : (firstKeyRaw as string | number)

  // React Aria keys its collection off each item's `id` PROP, not the React
  // `key`. Most callers pass dynamic options as <ListBoxItem key={x.id}> with no
  // id, so React Aria auto-generates keys (react-aria-1, ...) and the selected
  // value never matches the intended id (the select silently never commits).
  // Forward each child's `key` as its `id` when no explicit id is set, so a
  // bare `key={x.id}` works. Callers that already pass `id=` (e.g. the country
  // picker) are left untouched. A function (render-prop) child is passed through.
  const childrenWithIds =
    typeof children === 'function'
      ? children
      : Children.map(children as ReactNode, (child) => {
          if (
            isValidElement(child) &&
            child.key != null &&
            (child.props as { id?: Key }).id == null
          ) {
            return cloneElement(child as ReactElement<{ id?: Key }>, { id: String(child.key) })
          }
          return child
        })

  return (
    <Select
      selectedKey={firstKey}
      onSelectionChange={resolvedSelectionChange}
      isDisabled={isDisabled}
      isRequired={isRequired ?? required}
      isInvalid={isInvalid}
      // When a visible <Label> renders, React Aria derives the accessible name
      // from it; only fall back to an explicit aria-label when there is no
      // visible label (mirrors the Input wrapper). Without a label of either
      // kind, preserve the historical string-label aria fallback.
      aria-label={label ? undefined : ariaLabel}
      data-testid={testId}
      className={className}
      placeholder={placeholder}
      {...rest}
    >
      {label ? <Label>{label}</Label> : null}
      <SelectTrigger>
        <SelectValue />
      </SelectTrigger>
      <SelectPopover>
        <ListBox>
          {childrenWithIds as ReactNode}
        </ListBox>
      </SelectPopover>
    </Select>
  )
}

// Re-export ListBoxItem for convenience
export { ListBoxItem }
